import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Copy, KeyRound, X} from "lucide-react";
import {issueUserSupportCode} from "./api";
import {mapUserApiError} from "./errors";
import "../../shared/finance/financeOperations.css";
import {useTranslation} from "../../shared/i18n";


// Код живёт на экране 90 секунд, потом прячется сам.
const CODE_VISIBLE_MS = 90_000;

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

export const UserSupportCodeDialog = ({
    open,
    userId,
    entity,
    onCancel,
    onForbidden,
    onNotFound,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const invokingElementRef = useRef(null);
    const submittingRef = useRef(false);
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    // Код держим только в локальном стейте: не в queryClient, не в URL, без логов.
    const [issued, setIssued] = useState(null);
    const [rateLimited, setRateLimited] = useState(false);
    const [copied, setCopied] = useState(false);

    // Сброс на открытие и на смену пользователя — код одного юзера не должен
    // дожить до карточки другого.
    useEffect(() => {
        setReason("");
        setError(null);
        setIssued(null);
        setRateLimited(false);
        setCopied(false);
        setPending(false);
        submittingRef.current = false;
    }, [open, userId]);

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => firstFieldRef.current?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!submittingRef.current) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open]);

    useEffect(() => {
        if (!issued) return undefined;
        const timer = window.setTimeout(() => setIssued(null), CODE_VISIBLE_MS);
        return () => window.clearTimeout(timer);
    }, [issued]);

    if (!open) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (submittingRef.current || rateLimited) return;
        const trimmed = reason.trim();
        if (trimmed.length < 5) {
            setError({message: t("sections.users.p92")});
            return;
        }

        submittingRef.current = true;
        setPending(true);
        setError(null);
        try {
            const response = await issueUserSupportCode({userId, reason: trimmed});
            setIssued(response);
        } catch (requestError) {
            const status = requestError?.response?.status;
            if (status === 404) {
                onNotFound?.();
                onCancel();
                return;
            }
            if (status === 403) onForbidden?.();
            if (status === 429) setRateLimited(true);
            if (status === 503) {
                setError({message: t("sections.users.p93")});
            } else {
                setError(mapUserApiError(requestError));
            }
        } finally {
            submittingRef.current = false;
            setPending(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(issued.code);
            setCopied(true);
        } catch (_copyError) {
            setCopied(false);
        }
    };

    return createPortal(
        <div className="finance-dialog-backdrop">
            <section
                ref={dialogRef}
                className="finance-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
            >
                <form onSubmit={submit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{entity}</p>
                            <h2 id={titleId}>{t("sections.users.p38")}</h2>
                        </div>
                        <button
                            type="button"
                            className="finance-icon-button"
                            aria-label={t("sections.users.p5")}
                            title={t("sections.users.p5")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>

                    <p>{t("sections.users.p94")}</p>

                    <div aria-live="polite">
                        {issued ? (
                            <div className="support-code-result">
                                <p className="support-code-value">{issued.code}</p>
                                <p className="admin-card-note">
                                    {issued.name || entity} · {issued.phone_number || "—"}{t("sections.users.p95")}
                                </p>
                                <button type="button" className="admin-button admin-button-secondary" onClick={copyCode}>
                                    <Copy aria-hidden="true" size={15} /> {t("sections.users.p96")}
                                </button>
                                {copied ? <span className="admin-card-note">{t("sections.users.p97")}</span> : null}
                            </div>
                        ) : null}
                    </div>

                    {!issued ? (
                        <div className="finance-dialog-fields">
                            <label>
                                <span>{t("sections.users.p98")}</span>
                                <input
                                    ref={firstFieldRef}
                                    value={reason}
                                    disabled={pending}
                                    autoComplete="off"
                                    onChange={(event) => {
                                        setReason(event.target.value);
                                        setError(null);
                                    }}
                                />
                            </label>
                        </div>
                    ) : null}

                    {error?.message ? <div className="finance-dialog-error" role="alert">{error.message}</div> : null}

                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                            {issued ? t("sections.users.p99") : t("sections.users.p11")}
                        </button>
                        {!issued ? (
                            <button type="submit" className="admin-button admin-button-primary" disabled={pending || rateLimited}>
                                <KeyRound aria-hidden="true" size={15} /> {pending ? t("sections.users.p100") : t("sections.users.p101")}
                            </button>
                        ) : null}
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
